import type { FunctionDescriptorOutput } from '@festina/configuration-general-types/configuration'
import type { Optional } from '@festina-life/core'

/** The type definitions in this file correspond to the Festina meta model used by all the Festina services
 * at the time of writing. These are the building blocks of the entities described within Mudlr. */

export const MetaTypes = {
  SimpleMetaType: 'SimpleMetaType',
  ModelMetaType: 'ModelMetaType',
  EnumMetaType: 'EnumMetaType',
} as const

export type MetaModel = {
  types: Map<string, MetaType>
}

type CommonTypeFields = {
  typeName: string
  documentation: string
}

export type MetaType = EnumMetaType | SimpleMetaType | ModelMetaType

export type EnumMetaType = CommonTypeFields & {
  values: Optional<string[]>
  packageName: Optional<string>
  _type: typeof MetaTypes.EnumMetaType
}

export type SimpleMetaType = CommonTypeFields &
  Record<string, unknown> & { _type: typeof MetaTypes.SimpleMetaType }

export type ModelMetaType = CommonTypeFields & {
  superType: Optional<MetaType>
  superTypeName: Optional<string>
  subTypes: MetaType[]
  subTypeNames: string[]
  stereoTypes: string[]
  packageName: Optional<string>
  properties: PropertyDescriptor[]
  //Template only field to reach model
  modelType: Optional<ModelMetaType>
  modelTypeName: Optional<string>
  //Templated only field to reach template
  templateType: Optional<ModelMetaType>
  templateTypeName: Optional<string>
  configurationDetails: Optional<ConfigurationTypeDetails>
  _type: typeof MetaTypes.ModelMetaType
}

export type ConfigurationTypeDetails = {
  environmentTypeName: Optional<string>
  environment: Optional<ModelMetaType>
  defaultLibraryTypeNames: string[]
  defaultLibraries: ModelMetaType[]
  functions: FunctionDescriptorOutput[]
}

export type Cardinality = 'one' | 'many'

export type PropertyDescriptor = {
  documentation: string
  name: string
  type: Optional<MetaType>
  onlyTemplatedType: MetaType | null
  onlyTemplatedTypeName: string | null
  typeName: string
  stereoTypes: string[]
  cardinality: Cardinality
  association: 'composition' | 'aggregation'
}

/** Type guard function to detect when a MetaType is a ModelMetaType. Initially we're requiring all fields to have to
 * be defined in the type in order for it to be considered a ModelMetaType, however in the future we might want to test only
 * for the properties field. */
export const isModelMetaType = (
  metaType: Optional<MetaType>,
): metaType is ModelMetaType =>
  !!metaType && metaType._type === MetaTypes.ModelMetaType

/**
 * Type guard function to detect when a MetaType is an EnumMetaType.
 */
export const isEnumMetaType = (
  metaType: Optional<MetaType>,
): metaType is EnumMetaType =>
  !!metaType && metaType._type === MetaTypes.EnumMetaType

export const rootType = (field: ModelMetaType): string => {
  if (field.superType) {
    return rootType(field.superType as ModelMetaType)
  }

  return field.typeName
}
